import React from 'react';
import { motion } from 'framer-motion';

const CareerPage = () => {
  return (
    <div className="bg-white min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            We're looking for talented individuals who are passionate about data, AI, and building the future of 
            data observability. Together, we're solving the most complex challenges in modern data ecosystems.
          </p>
        </motion.div>
      </section>
      
      {/* Company Culture Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Why Work With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold mb-3">Innovative Technology</h3>
              <p className="text-gray-600">Work with cutting-edge technology in machine learning, data science, and distributed systems.</p>
            </motion.div>
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold mb-3">Collaborative Culture</h3>
              <p className="text-gray-600">Join a team that values open communication, diversity of thought, and mutual support.</p>
            </motion.div>
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold mb-3">Growth Opportunities</h3>
              <p className="text-gray-600">Develop your skills and advance your career in a fast-growing company that invests in its people.</p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Open Positions Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Open Positions</h2>
          
          {/* Senior ML Engineer Position */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white p-8 rounded-xl shadow-md mb-8 border border-gray-200"
          >
            <h3 className="text-2xl font-bold mb-4 text-blue-700">Senior Machine Learning Engineer</h3>
            <div className="flex flex-wrap gap-4 mb-6">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Full-time</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Engineering</span>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">About the Role</h4>
              <p className="text-gray-700 mb-4">
                We are seeking a Senior Machine Learning Engineer to join our engineering team. In this role, you'll design and implement advanced ML solutions, with a specific focus on anomaly detection and causal models. You'll be working at the forefront of data observability, helping our customers identify issues in their data pipelines and understand root causes.
              </p>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Responsibilities</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Design, build, and deploy machine learning models focused on anomaly detection and causal inference</li>
                <li>Develop scalable ML pipelines to process large volumes of data efficiently</li>
                <li>Research and implement state-of-the-art ML algorithms to improve our data monitoring capabilities</li>
                <li>Work closely with data engineering and product teams to integrate ML solutions into our platform</li>
                <li>Contribute to the development of our ML infrastructure and tooling</li>
                <li>Mentor junior engineers and foster a culture of knowledge sharing</li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Requirements</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>5+ years of experience in machine learning development</li>
                <li>Strong background in statistical methods and time series analysis</li>
                <li>Experience with anomaly detection and/or causal inference techniques</li>
                <li>Proficiency in Python and ML frameworks (TensorFlow, PyTorch, scikit-learn)</li>
                <li>Experience deploying ML models to production</li>
                <li>Strong software engineering practices</li>
                <li>MS or PhD in Computer Science, Machine Learning, Statistics, or related field</li>
              </ul>
            </div>
            
            <div>
              <a 
                href="/apply/senior-ml-engineer" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-200"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
          
          {/* GenAI Engineer Position */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white p-8 rounded-xl shadow-md mb-8 border border-gray-200"
          >
            <h3 className="text-2xl font-bold mb-4 text-purple-700">GenAI Engineer</h3>
            <div className="flex flex-wrap gap-4 mb-6">
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">Full-time</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Engineering</span>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">About the Role</h4>
              <p className="text-gray-700 mb-4">
                We're looking for a Generative AI Engineer to help build and enhance our AI-powered data observability features. In this role, you'll focus on developing agentic AI systems that autonomously identify, diagnose, and recommend solutions to data issues. You'll be at the intersection of LLMs, data analytics, and engineering, building the next generation of intelligent data tools.
              </p>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Responsibilities</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Develop and implement agentic AI systems that can autonomously navigate data ecosystems</li>
                <li>Design and build LLM-powered features for data analysis, insights generation, and issue resolution</li>
                <li>Create intelligent interfaces that make complex data insights accessible</li>
                <li>Optimize model prompting strategies and fine-tune models for specific data tasks</li>
                <li>Research and implement techniques to ensure responsible and unbiased AI behavior</li>
                <li>Collaborate with UX and product teams to create intuitive AI-powered experiences</li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Requirements</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>3+ years of experience in AI/ML development</li>
                <li>Experience with generative AI systems, particularly large language models</li>
                <li>Knowledge of prompt engineering and LLM fine-tuning</li>
                <li>Understanding of agentic AI architectures and autonomous systems</li>
                <li>Proficiency in Python and relevant AI frameworks</li>
                <li>Familiarity with data engineering concepts and tools</li>
                <li>BS or MS in Computer Science, AI, or related field</li>
              </ul>
            </div>
            
            <div>
              <a 
                href="/apply/genai-engineer" 
                className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-200"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Join Us CTA Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Don't See Your Perfect Role?</h2>
          <p className="text-xl text-gray-600 mb-8">
            We're always looking for talented individuals to join our team. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <a 
            href="mailto:careers@qupid.ai" 
            className="inline-block bg-gray-900 hover:bg-gray-800 text-white font-semibold px-6 py-3 rounded-lg transition duration-200"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default CareerPage; 